import { useState, type FormEvent } from "react";
import { Button, Field, FormDialog, DialogCancel } from "@cntrl/ui";
import { useChangePassword } from "../../app/hooks/useAccount.js";
import { ApiError } from "../../app/hooks/apiError.js";
import styles from "./ChangePasswordModal.module.scss";

export interface ChangePasswordModalProps {
  onClose: () => void;
}

/**
 * Change password — the ONE implementation, opened from the band Settings modal's Account tab and the org
 * Settings modal's.
 *
 * It is PERSON-scoped: `POST /auth/change-password` takes no tenant, and the fresh token it returns replaces
 * the caller's session through the TokenProvider (in `useChangePassword`). So it needs neither BandContext
 * nor OrgContext, which is exactly why one component can serve both shells — the surface either side wraps
 * it in is the only thing that ever differed. It existed twice until 2026-09-01, byte-for-byte.
 *
 * A DIALOG rather than an inline form (PO, 2026-09-06). It is a discrete task with its own commit point, and
 * inline it sat permanently expanded under the account details with three empty boxes and a live submit —
 * an interruption in a tab you open to read your email address. The fields are STACKED here, not the
 * 3-across `FormRow` they used to be: three short controls side by side in a narrow card read as a row of
 * stubs rather than one form.
 *
 * The modal is opened FROM another modal, which needs nothing special — `Modal` owns the Escape stack, so
 * this one closes first and leaves Settings open behind it. It is a `FormDialog`: a bottom sheet on a phone,
 * where a centred card with the keyboard up covers the fields (ART-251).
 *
 * SUCCESS CLOSES THE DIALOG; there is no "Password updated." line, because the inline version needed one
 * only to say something had happened in a form that stayed on screen. A dialog that dismisses has said it.
 * A wrong current password still surfaces the server's 4xx verbatim, inline — never a phantom success.
 */
export function ChangePasswordModal({ onClose }: ChangePasswordModalProps) {
  const changePassword = useChangePassword();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!current || !next) {
      setFormError("Enter your current and new password.");
      return;
    }
    if (next !== confirm) {
      setFormError("The new passwords don't match.");
      return;
    }
    changePassword.mutate(
      { current, newPassword: next },
      {
        onSuccess: onClose,
        onError: (err) =>
          setFormError(err instanceof ApiError && err.message ? err.message : "Couldn't change your password."),
      },
    );
  }

  return (
    <FormDialog
      title="Change password"
      size="sm"
      onClose={onClose}
      bodyAs="form"
      onSubmit={onSubmit}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" loading={changePassword.isPending} disabled={!current || !next}>
            Update password
          </Button>
        </>
      }
    >
      <div className={styles.fields}>
        <Field
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <Field
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {formError && (
          <p className={styles.error} role="alert">
            {formError}
          </p>
        )}
      </div>
    </FormDialog>
  );
}
